import type {
  ReviewPartSchemaDetails,
  ReviewPartSchema as TeamReviewPartSchema,
} from 'bundles/assess-common/types/NaptimeReviewSchema';
import type { Git, SubmissionPartSchema } from 'bundles/assess-common/types/NaptimeSubmissionSchema';
import type { ProctorConfigOverrideQuery_Proctor_getLatestOverrideForAssignmentSubmitter as ProctorConfigOverride } from 'bundles/author-assess-common/data/proctoring/__generated__/ProctorConfigOverrideQuery';
import type {
  ReviewPartSchemaValidation as TeamReviewPartSchemaValidation,
  SubmissionPartSchemaValidation as TeamSubmissionPartSchemaValidation,
} from 'bundles/author-assess-common/types/PartSchemaValidation';
import type { AtomicValidation } from 'bundles/author-common/types/AuthoringValidation';
import { newCMLBlock } from 'bundles/author-common/utils/SubmissionSchemaUtils';
import type { ReviewSchemaPart } from 'bundles/author-peer-common/types/ReviewSchema';
import type { SubmissionSchemaPart } from 'bundles/author-peer-common/types/SubmissionSchema';
import type {
  ReviewPartSchemaDraftOptionValidation,
  ReviewPartSchemaDraftValidation,
  SubmissionSchemaPartDraftValidation,
} from 'bundles/author-peer-common/types/Validation';
import type { ProctorConfigOverrideDraft } from 'bundles/authoring/common/types/proctorConfig';
import type { CmlContent, Content } from 'bundles/cml';

type FileUploadSchema = {
  required: boolean;
};

type UrlSchema = {
  allowedUrls: Array<AllowedUrlType>;
};

type AllowedUrlType = 'All' | 'StandardAudio' | 'StandardVideo';

const ensureContentIsCmlContent = (content: Content): CmlContent => {
  if (typeof content === 'string') {
    return newCMLBlock(`<co-content><text>${content}</text></co-content>`);
  } else if (content.typeName === 'html') {
    return newCMLBlock(`<co-content><text>${content.definition}</text></co-content>`);
  } else {
    return content;
  }
};

export function convertTeamReviewSchemaToPeer(part: TeamReviewPartSchema): ReviewSchemaPart {
  let isScored = false;
  let options: Array<{
    id: string;
    display: CmlContent;
    points: number;
  }> = [];
  let yesNoPoints = {
    yes: 0,
    no: 0,
  };
  let multiLineInputPoints: number | undefined;

  if (part.details.typeName === 'options') {
    ({ isScored } = part.details.definition);
    options = part.details.definition.options.map((option) => ({
      id: option.id,
      display: ensureContentIsCmlContent(option.display),
      points: option.points || 0,
    }));
  } else if (part.details.typeName === 'yesNo') {
    isScored = true;
    yesNoPoints = Object.assign({}, part.details.definition.points);
  } else if (part.details.typeName === 'multiLineInput' && part.details.definition.points) {
    isScored = true;
    multiLineInputPoints = part.details.definition.points;
  }

  return {
    id: part.id,
    prompt: ensureContentIsCmlContent(part.prompt),
    typeName: part.details.typeName,
    isScored,
    options,
    yesNoPoints,
    multiLineInputPoints,
  };
}

export function convertPeerReviewSchemaToTeam(
  reviewSchemaPart: ReviewSchemaPart,
  submissionPartId?: string
): TeamReviewPartSchema {
  let details: ReviewPartSchemaDetails;
  if (reviewSchemaPart.typeName === 'multiLineInput') {
    let definition = {};
    if (reviewSchemaPart.isScored) {
      definition = {
        points: reviewSchemaPart.multiLineInputPoints || 5,
      };
    }

    details = {
      typeName: 'multiLineInput',
      definition,
    };
  } else if (reviewSchemaPart.typeName === 'options') {
    details = {
      typeName: 'options',
      definition: {
        isScored: reviewSchemaPart.isScored,
        options: reviewSchemaPart.options.map((option) => ({
          id: option.id,
          display: option.display,
          points: option.points,
        })),
      },
    };
  } else if (reviewSchemaPart.typeName === 'singleLineInput') {
    details = {
      typeName: 'singleLineInput',
      definition: {},
    };
  } else if (reviewSchemaPart.typeName === 'boxViewDocumentAnnotation') {
    details = {
      typeName: 'boxViewDocumentAnnotation',
      definition: {},
    };
  } else {
    details = {
      typeName: 'yesNo',
      definition: {
        points: {
          yes: reviewSchemaPart.yesNoPoints.yes,
          no: reviewSchemaPart.yesNoPoints.no,
        },
      },
    };
  }

  return {
    id: reviewSchemaPart.id,
    submissionPartId,
    prompt: reviewSchemaPart.prompt,
    details,
  };
}

const makePassingValidation = (): AtomicValidation => {
  return {
    typeName: 'genericValidation',
    status: 'VALID',
  };
};

export function convertTeamReviewSchemaValidationToPeer(
  validation: TeamReviewPartSchemaValidation
): ReviewPartSchemaDraftValidation {
  let isScored = makePassingValidation();
  let allOptions = makePassingValidation();

  let options: Array<ReviewPartSchemaDraftOptionValidation> = [];

  if (validation.details.typeName === 'options') {
    ({ isScored } = validation.details.definition);

    allOptions = validation.details.definition.optionsCollection;

    options = validation.details.definition.options.map((optionValidation) => ({
      id: optionValidation.id,
      display: optionValidation.display,
      points: makePassingValidation(),
    }));
  }

  return {
    id: validation.id,
    prompt: validation.prompt,
    typeName: makePassingValidation(),
    options,
    allOptions,
    yesNoPoints: {
      yes: makePassingValidation(),
      no: makePassingValidation(),
    },
    isScored,
    maxScore: makePassingValidation(),
  };
}

export function convertTeamSubmissionSchemaToPeer(
  part: SubmissionPartSchema,
  reviewParts: Array<TeamReviewPartSchema>,
  peerReviewParts?: Array<TeamReviewPartSchema>
): SubmissionSchemaPart {
  let uploadRequired = false;
  let allowedUrlTypes: AllowedUrlType[] = ['All'];
  const { typeName, definition } = part.details;

  if (typeName === 'fileUpload') {
    uploadRequired = (definition as FileUploadSchema).required;
  } else if (typeName === 'url') {
    allowedUrlTypes = (definition as UrlSchema).allowedUrls;
  }

  const convertedReviewParts = reviewParts
    .filter((reviewPart) => reviewPart.submissionPartId === part.id)
    .map(convertTeamReviewSchemaToPeer);

  const convertedPeerReviewParts = peerReviewParts
    ? peerReviewParts
        .filter((peerReviewPart) => peerReviewPart.submissionPartId === part.id)
        .map(convertTeamReviewSchemaToPeer)
    : null;

  return {
    id: part.id,
    typeName,
    definition,
    prompt: ensureContentIsCmlContent(part.prompt),
    allowedUrlTypes,
    uploadRequired,
    plagiarismConfig: part.plagiarismConfig,
    reviewSchemaParts: convertedReviewParts,
    ...(convertedPeerReviewParts ? { peerReviewSchemaParts: convertedPeerReviewParts } : {}),
  };
}

export function convertTeamSubmissionSchemaValidationToPeer(
  validation: TeamSubmissionPartSchemaValidation | undefined | null,
  reviewValidations: Array<TeamReviewPartSchemaValidation>,
  peerReviewValidations: Array<TeamReviewPartSchemaValidation> = []
): SubmissionSchemaPartDraftValidation | undefined | null {
  if (!validation) {
    return null;
  }

  return {
    id: validation.id,
    typeName: makePassingValidation(),
    prompt: validation.prompt,
    allowedUrlTypes: makePassingValidation(),
    uploadRequired: makePassingValidation(),
    reviewSchemaParts: reviewValidations.map(convertTeamReviewSchemaValidationToPeer),
    peerReviewSchemaParts: peerReviewValidations.map(convertTeamReviewSchemaValidationToPeer),
  };
}

export function convertPeerSubmissionSchemaToTeam(part: SubmissionSchemaPart): SubmissionPartSchema {
  const { id, prompt, plagiarismConfig } = part;
  const partSchemaWithoutDetails = {
    id,
    prompt,
    plagiarismConfig,
  };

  if (part.typeName === 'fileUpload') {
    return {
      ...partSchemaWithoutDetails,
      details: {
        typeName: 'fileUpload',
        definition: {
          required: part.uploadRequired,
        },
      },
    };
  } else if (part.typeName === 'plainText') {
    return {
      ...partSchemaWithoutDetails,
      details: {
        typeName: 'plainText',
        definition: {},
      },
    };
  } else if (part.typeName === 'richText') {
    return {
      ...partSchemaWithoutDetails,
      details: {
        typeName: 'richText',
        definition: {},
      },
    };
  } else if (part.typeName === 'offPlatform') {
    return {
      ...partSchemaWithoutDetails,
      details: {
        typeName: 'offPlatform',
        definition: {},
      },
    };
  } else if (part.typeName === 'git') {
    const gitPart: Git = part as Git;
    return {
      ...partSchemaWithoutDetails,
      details: {
        typeName: 'git',
        definition: gitPart.definition,
      },
    };
  } else {
    return {
      ...partSchemaWithoutDetails,
      details: {
        typeName: 'url',
        definition: {
          allowedUrls: part.allowedUrlTypes,
        },
      },
    };
  }
}

export function convertDGSProctorConfigDataToProctorConfigDraft(
  proctorConfigurationId: string,
  proctorConfigOverride?: ProctorConfigOverride
): ProctorConfigOverrideDraft | undefined {
  if (!proctorConfigOverride || !proctorConfigurationId) return undefined;

  return {
    id: proctorConfigurationId,
    proctorConfigurationId,
    draft: {
      maxAttempts: proctorConfigOverride?.maxAttempts ?? undefined,
      createdAt: Number(proctorConfigOverride?.createdTimestamp?.seconds || 0) * 1000,
      attemptConfigurations: [
        {
          maxSubmissions: proctorConfigOverride?.maxSubmissionsPerAttempt ?? undefined,
          timeLimit: {
            duration: proctorConfigOverride?.attemptDuration ?? 0,
            gracePeriod: 0,
            lateSubmissionPolicy: {
              'org.coursera.proctor.configuration.MustSubmitLastSavedVersionAtTimeLimit': {},
            },
          },
        },
      ],
    },
  };
}
